import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, PoundSterling } from "lucide-react";
import { Reveal } from "./Reveal";

const fmt0 = (n) => `£${Math.round(n).toLocaleString("en-GB")}`;
const pct = (n) => `${(n * 100).toFixed(1)}%`;

const clean = (v) => Number(String(v).replace(/[^\d.]/g, "")) || 0;

const useThousands = (initial = "") => {
  const [str, setStr] = useState(initial);
  const onChange = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
    setStr(digits ? Number(digits).toLocaleString("en-GB") : "");
  };
  return [str, onChange, clean(str)];
};

const Row = ({ label, value, highlight, muted }) => (
  <div className="flex items-baseline justify-between py-3.5 border-b border-cream/10 last:border-b-0">
    <span className="text-[11.5px] tracking-[0.18em] uppercase font-bold text-cream/70">{label}</span>
    <span
      className={`font-serif ${
        highlight
          ? "text-3xl sm:text-[32px] text-gold-soft"
          : muted
            ? "text-base text-cream/70"
            : "text-xl text-cream"
      }`}
    >
      {value}
    </span>
  </div>
);

const Toggle = ({ value, onChange }) => (
  <div
    role="tablist"
    aria-label="Yield mode"
    className="inline-flex border-2 border-forest bg-white overflow-hidden"
    data-testid="yield-mode-toggle"
  >
    {[
      { v: "gross", label: "Gross" },
      { v: "net", label: "Net" },
    ].map((o, i) => (
      <button
        key={o.v}
        type="button"
        role="tab"
        aria-selected={value === o.v}
        data-testid={`yield-mode-${o.v}`}
        onClick={() => onChange(o.v)}
        className={`min-w-[96px] px-5 py-2.5 text-[12px] font-bold tracking-[0.18em] uppercase transition-colors duration-300 ${
          i === 0 ? "border-r-2 border-forest" : ""
        } ${
          value === o.v ? "bg-forest text-cream" : "bg-white text-forest hover:bg-cream-2"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const HmoYieldSnapshot = () => {
  const [mode, setMode] = useState("gross");
  const [priceStr, onPrice, price] = useThousands("");
  const [rentStr, onRent, weeklyRent] = useThousands("");
  const [rooms, setRooms] = useState(5);
  const [voidWeeks, setVoidWeeks] = useState(2);

  // Net-mode inputs
  const [mortgageStr, onMortgage, monthlyMortgage] = useThousands("");
  const [billsStr, onBills, monthlyBills] = useThousands("");
  const [mgmtPct, setMgmtPct] = useState(12);
  const [maintPct, setMaintPct] = useState(5);

  const result = useMemo(() => {
    if (price <= 0 || weeklyRent <= 0 || rooms <= 0) return null;
    const occupiedWeeks = Math.max(0, 52 - voidWeeks);
    const annualPerRoom = weeklyRent * occupiedWeeks;
    const grossAnnual = annualPerRoom * rooms;
    const grossMonthly = grossAnnual / 12;
    const grossYield = grossAnnual / price;

    if (mode === "gross") {
      return {
        mode,
        gross: grossYield,
        headlineMonthly: grossMonthly,
        annual: grossAnnual,
        annualPerRoom,
        occupiedWeeks,
      };
    }

    const mgmtAnnual = grossAnnual * (mgmtPct / 100);
    const maintAnnual = grossAnnual * (maintPct / 100);
    const mortgageAnnual = monthlyMortgage * 12;
    const billsAnnual = monthlyBills * 12;
    const totalCostsAnnual = mortgageAnnual + billsAnnual + mgmtAnnual + maintAnnual;
    const netAnnual = grossAnnual - totalCostsAnnual;
    const netMonthly = netAnnual / 12;
    const netYield = netAnnual / price;

    return {
      mode,
      gross: grossYield,
      net: netYield,
      headlineMonthly: netMonthly,
      annual: grossAnnual,
      netAnnual,
      mortgageAnnual,
      billsAnnual,
      mgmtAnnual,
      maintAnnual,
      occupiedWeeks,
    };
  }, [price, weeklyRent, rooms, voidWeeks, mode, monthlyMortgage, monthlyBills, mgmtPct, maintPct]);

  const headlineLabel = mode === "net" ? "Net Rental Yield" : "Gross Rental Yield";
  const headlineValue = mode === "net" ? result?.net : result?.gross;
  const isNetNegative = mode === "net" && result && result.net < 0;

  return (
    <section data-testid="hmo-yield" className="bg-cream border-y border-forest/10">
      <div className="wrap-x py-20 sm:py-24">
        <Reveal>
          <div className="max-w-2xl">
            <p className="eyebrow text-gold-dark">Rentable Yield Snapshot</p>
            <h2 className="font-serif text-4xl sm:text-[44px] font-medium text-forest leading-[1.1] mt-5 mb-5">
              Monthly Room Income vs Purchase Price
            </h2>
            <p className="text-[14.5px] leading-[1.8] text-sage mb-6">
              A back‑of‑envelope indicator for an HMO opportunity. Flip to <strong>Net</strong> to
              factor in mortgage, management, bills and maintenance. Not a valuation; always run
              full numbers with a professional before you commit.
            </p>
            <Toggle value={mode} onChange={setMode} />
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-start">
          <Reveal>
            <div className="bg-cream-3 border border-forest/10 p-7 sm:p-10 space-y-7">
              <div>
                <label htmlFor="yield-price" className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5">
                  Purchase Price
                </label>
                <div className="relative">
                  <PoundSterling size={16} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest/60" />
                  <input
                    id="yield-price"
                    data-testid="yield-price-input"
                    inputMode="numeric"
                    value={priceStr}
                    onChange={onPrice}
                    placeholder="450,000"
                    className="w-full bg-white border border-forest/20 pl-10 pr-4 py-3.5 text-base text-ink font-medium focus:border-gold focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="yield-rooms" className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5">
                    Lettable Rooms
                  </label>
                  <input
                    id="yield-rooms"
                    data-testid="yield-rooms-input"
                    type="number"
                    min="1"
                    max="20"
                    value={rooms}
                    onChange={(e) => setRooms(Math.max(1, Math.min(20, Number(e.target.value) || 0)))}
                    className="w-full bg-white border border-forest/20 px-4 py-3.5 text-base text-ink font-medium focus:border-gold focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="yield-rent" className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5">
                    Avg Weekly Rent / Room
                  </label>
                  <div className="relative">
                    <PoundSterling size={16} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest/60" />
                    <input
                      id="yield-rent"
                      data-testid="yield-rent-input"
                      inputMode="numeric"
                      value={rentStr}
                      onChange={onRent}
                      placeholder="150"
                      className="w-full bg-white border border-forest/20 pl-10 pr-4 py-3.5 text-base text-ink font-medium focus:border-gold focus:outline-none transition-colors"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="yield-voids" className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5">
                  Void Allowance — {voidWeeks} week{voidWeeks === 1 ? "" : "s"} per room / year
                </label>
                <input
                  id="yield-voids"
                  data-testid="yield-voids-input"
                  type="range"
                  min="0"
                  max="12"
                  value={voidWeeks}
                  onChange={(e) => setVoidWeeks(Number(e.target.value))}
                  className="w-full accent-forest"
                />
                <div className="flex justify-between text-[10px] tracking-[0.2em] uppercase text-sage mt-1.5">
                  <span>0</span>
                  <span>6</span>
                  <span>12</span>
                </div>
              </div>

              {mode === "net" && (
                <div data-testid="yield-net-inputs" className="pt-6 border-t border-forest/15 space-y-6">
                  <p className="eyebrow text-gold-dark !mb-0">Running Costs</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="yield-mortgage" className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5">
                        Monthly Mortgage
                      </label>
                      <div className="relative">
                        <PoundSterling size={16} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest/60" />
                        <input
                          id="yield-mortgage"
                          data-testid="yield-mortgage-input"
                          inputMode="numeric"
                          value={mortgageStr}
                          onChange={onMortgage}
                          placeholder="1,500"
                          className="w-full bg-white border border-forest/20 pl-10 pr-4 py-3.5 text-base text-ink font-medium focus:border-gold focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="yield-bills" className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5">
                        Monthly Bills (HMO)
                      </label>
                      <div className="relative">
                        <PoundSterling size={16} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest/60" />
                        <input
                          id="yield-bills"
                          data-testid="yield-bills-input"
                          inputMode="numeric"
                          value={billsStr}
                          onChange={onBills}
                          placeholder="450"
                          className="w-full bg-white border border-forest/20 pl-10 pr-4 py-3.5 text-base text-ink font-medium focus:border-gold focus:outline-none transition-colors"
                        />
                      </div>
                      <p className="text-[11px] text-sage mt-2 leading-relaxed">
                        All‑inclusive HMOs usually cover utilities, broadband and council tax.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="yield-mgmt" className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5">
                        Management Fee — {mgmtPct}%
                      </label>
                      <input
                        id="yield-mgmt"
                        data-testid="yield-mgmt-input"
                        type="range"
                        min="0"
                        max="20"
                        value={mgmtPct}
                        onChange={(e) => setMgmtPct(Number(e.target.value))}
                        className="w-full accent-forest"
                      />
                      <div className="flex justify-between text-[10px] tracking-[0.2em] uppercase text-sage mt-1.5">
                        <span>0%</span>
                        <span>10%</span>
                        <span>20%</span>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="yield-maint" className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2.5">
                        Maintenance Allowance — {maintPct}%
                      </label>
                      <input
                        id="yield-maint"
                        data-testid="yield-maint-input"
                        type="range"
                        min="0"
                        max="20"
                        value={maintPct}
                        onChange={(e) => setMaintPct(Number(e.target.value))}
                        className="w-full accent-forest"
                      />
                      <div className="flex justify-between text-[10px] tracking-[0.2em] uppercase text-sage mt-1.5">
                        <span>0%</span>
                        <span>10%</span>
                        <span>20%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-[12px] leading-relaxed text-sage">
                {mode === "net"
                  ? "Net yield subtracts mortgage, bills, management and a maintenance allowance from gross rent. Tax, licensing renewals and refurbishment reserves aren't included — we'll build a full projection with you."
                  : "A gross yield uses rental income before mortgage, management, bills, licensing and maintenance costs. Flip to Net to see the picture with running costs factored in."}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div
              data-testid="yield-results"
              className="bg-forest text-cream p-7 sm:p-10 relative overflow-hidden grain"
            >
              <div className="relative z-[2]">
                <div className="flex items-center gap-3 mb-5">
                  <TrendingUp size={18} strokeWidth={1.6} className="text-gold-soft" />
                  <p className="eyebrow text-gold-soft !mb-0">Your Snapshot — {mode === "net" ? "Net" : "Gross"}</p>
                </div>
                {!result ? (
                  <p className="text-cream/65 text-sm leading-relaxed mt-6 max-w-sm">
                    Enter a purchase price and an average weekly rent per room — a yield and
                    monthly income estimate will appear here instantly.
                  </p>
                ) : (
                  <>
                    <div className="mt-4 mb-6">
                      <p
                        data-testid={mode === "net" ? "yield-net" : "yield-gross"}
                        className={`font-serif text-5xl sm:text-6xl leading-none ${
                          isNetNegative ? "text-[#E7B99F]" : "text-gold-soft"
                        }`}
                      >
                        {pct(headlineValue)}
                      </p>
                      <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-cream/60 mt-3">
                        {headlineLabel}
                      </p>
                      {mode === "net" && (
                        <p className="text-[12px] text-cream/60 mt-2">
                          Gross yield equivalent: <span className="text-gold-soft">{pct(result.gross)}</span>
                        </p>
                      )}
                      {isNetNegative && (
                        <p className="text-[12.5px] leading-relaxed text-[#f1c6ad] mt-3 max-w-xs">
                          Costs currently exceed income — adjust your assumptions or revisit the deal with us.
                        </p>
                      )}
                    </div>
                    <div>
                      <Row
                        label={mode === "net" ? "Net monthly income" : "Monthly rental income"}
                        value={fmt0(result.headlineMonthly)}
                        highlight
                      />
                      <Row label="Annual rent (gross)" value={fmt0(result.annual)} />
                      {mode === "net" && (
                        <>
                          <Row label="Mortgage / year" value={`-${fmt0(result.mortgageAnnual)}`} muted />
                          <Row label="Bills / year" value={`-${fmt0(result.billsAnnual)}`} muted />
                          <Row label={`Management (${mgmtPct}%)`} value={`-${fmt0(result.mgmtAnnual)}`} muted />
                          <Row label={`Maintenance (${maintPct}%)`} value={`-${fmt0(result.maintAnnual)}`} muted />
                          <Row label="Net annual income" value={fmt0(result.netAnnual)} />
                        </>
                      )}
                      {mode === "gross" && (
                        <>
                          <Row label="Per room / year" value={fmt0(result.annualPerRoom)} />
                          <Row label="Occupied weeks assumed" value={`${result.occupiedWeeks} / 52`} />
                        </>
                      )}
                    </div>
                  </>
                )}

                <div className="mt-8">
                  <Link to="/contact" data-testid="yield-cta" className="btn-gold">
                    Model This With Us <ArrowRight size={14} strokeWidth={2.2} />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default HmoYieldSnapshot;
